/** Apply color / size / font to the current contentEditable selection. */

export function editingTextRoot(elId) {
  if (typeof document === 'undefined') return null;
  const active = document.activeElement;
  if (!active?.isContentEditable) return null;
  if (elId && !active.closest?.(`[data-id="${String(elId)}"]`)) return null;
  return active;
}

export function hasNonCollapsedSelection(root) {
  if (typeof window === 'undefined') return false;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false;
  if (root && sel.anchorNode && !root.contains(sel.anchorNode)) return false;
  return true;
}

export function applyCssToSelection(styleObj) {
  if (typeof window === 'undefined') return false;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false;
  const range = sel.getRangeAt(0);
  const span = document.createElement('span');
  Object.assign(span.style, styleObj);
  try {
    range.surroundContents(span);
  } catch {
    const frag = range.extractContents();
    span.appendChild(frag);
    range.insertNode(span);
  }
  sel.removeAllRanges();
  const next = document.createRange();
  next.selectNodeContents(span);
  sel.addRange(next);
  return true;
}

export function execOnSelection(cmd, value) {
  if (typeof document === 'undefined') return false;
  try {
    return document.execCommand(cmd, false, value == null ? null : value);
  } catch {
    return false;
  }
}

export function htmlFromEditable(root) {
  if (!root) return '';
  return root.innerHTML || '';
}

export function plainFromEditable(root) {
  if (!root) return '';
  return root.innerText || root.textContent || '';
}

function pxFromNode(node, root) {
  const el = node && node.nodeType === 1 ? node : node?.parentElement;
  if (!el || (root && !root.contains(el) && el !== root)) return null;
  const px = parseFloat(window.getComputedStyle(el).fontSize);
  if (!Number.isFinite(px) || px <= 0) return null;
  return Math.round(px);
}

/**
 * Font size (px) at the caret, or the shared size of a selection.
 * Mixed sizes → null (caller keeps the box size).
 */
export function uniformSelectionFontSize(root) {
  if (!root || typeof window === 'undefined') return null;
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return null;
  if (sel.anchorNode && !root.contains(sel.anchorNode)) return null;
  const range = sel.getRangeAt(0);
  const sizes = new Set();
  if (sel.isCollapsed) {
    const n = pxFromNode(sel.anchorNode, root);
    return n;
  }
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    const raw = node.textContent || '';
    if (raw.replace(/[\s\u200b]/g, '') && range.intersectsNode(node)) {
      const n = pxFromNode(node, root);
      if (n) sizes.add(n);
    }
    node = walker.nextNode();
  }
  if (!sizes.size) {
    const n = pxFromNode(sel.anchorNode, root);
    return n;
  }
  if (sizes.size === 1) return [...sizes][0];
  return null;
}

/** Drop inline font-size so the box size applies to every run. */
export function stripInlineFontSizes(html) {
  const src = String(html || '');
  if (!src) return src;
  if (typeof document === 'undefined') {
    return src.replace(/font-size\s*:\s*[^;]+;?/gi, '');
  }
  const tmp = document.createElement('div');
  tmp.innerHTML = src;
  tmp.querySelectorAll('[style]').forEach((node) => {
    const st = node.getAttribute('style') || '';
    if (!/font-size\s*:/i.test(st)) return;
    const cleaned = st
      .replace(/font-size\s*:\s*[^;]+;?/gi, '')
      .replace(/;;+/g, ';')
      .replace(/^;+|;+$/g, '')
      .trim();
    if (cleaned) node.setAttribute('style', cleaned);
    else node.removeAttribute('style');
  });
  tmp.querySelectorAll('font[size]').forEach((node) => node.removeAttribute('size'));
  return tmp.innerHTML;
}
